import type { ConsumerApiClient } from '../client';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface SignUpRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
}

/**
 * Notification category preferences. Matches the mobile app's toggle set
 * (`apps/pelipost-mobile/src/screens/profile/NotificationPreferencesScreen.tsx`).
 *
 * `order_updates` and `support` are transactional — the backend sends them
 * regardless of the preference — but we still surface them in the UI so
 * users understand what they can and cannot opt out of.
 */
export interface NotificationPreferences {
  order_updates: boolean;
  marketing: boolean;
  engagement: boolean;
  cart_and_drafts: boolean;
  points: boolean;
  support: boolean;
}

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    phone?: string;
    pelipoints_balance: number;
    wallet_balance_cents: number;
    mailingAddress?: {
      line1: string;
      line2?: string;
      city: string;
      state: string;
      zip: string;
    };
    notificationPreferences?: Partial<NotificationPreferences>;
  };
}

export interface VerifyPhoneRequest {
  code: string;
}

export interface CheckEmailResponse {
  exists: boolean;
}

export interface UpdateProfileRequest {
  firstName?: string;
  lastName?: string;
  phone?: string;
  mailingAddress?: { line1: string; line2?: string; city: string; state: string; zip: string };
}

export interface AuthApi {
  checkEmail(email: string): Promise<CheckEmailResponse>;
  login(data: LoginRequest): Promise<AuthResponse>;
  signUp(data: SignUpRequest): Promise<AuthResponse>;
  verifyPhone(data: VerifyPhoneRequest): Promise<{ verified: boolean }>;
  requestPhoneVerification(): Promise<{ sent: boolean }>;
  refreshToken(refreshToken: string): Promise<AuthResponse>;
  getProfile(): Promise<AuthResponse['user']>;
  updateProfile(data: UpdateProfileRequest): Promise<AuthResponse['user']>;
  /**
   * Partial update of notification preferences. Pass only the toggles that
   * are changing — the backend merges them with the existing prefs.
   * PATCH /auth/profile with `{ notificationPreferences: { [key]: bool } }`
   * matches the mobile app pattern.
   */
  updateNotificationPreferences(
    prefs: Partial<NotificationPreferences>,
  ): Promise<AuthResponse['user']>;
  logout(): Promise<void>;
  forgotPassword(email: string): Promise<{ sent: boolean }>;
}

export function createAuthApi(client: ConsumerApiClient): AuthApi {
  return {
    checkEmail(email) {
      return client.post('/auth/check-email', { email }, { skipAuth: true });
    },
    login(data) {
      return client.post('/auth/login', data, { skipAuth: true });
    },
    signUp(data) {
      return client.post('/auth/register', data, { skipAuth: true });
    },
    verifyPhone(data) {
      return client.post('/auth/verify-phone', data);
    },
    requestPhoneVerification() {
      return client.post('/auth/request-phone-verification');
    },
    refreshToken(refreshToken) {
      return client.post('/auth/refresh', { refreshToken }, { skipAuth: true });
    },
    getProfile() {
      return client.get('/auth/profile');
    },
    updateProfile(data) {
      return client.put('/auth/profile', data);
    },
    updateNotificationPreferences(prefs) {
      return client.patch('/auth/profile', { notificationPreferences: prefs });
    },
    logout() {
      return client.post('/auth/logout');
    },
    forgotPassword(email) {
      return client.post('/auth/forgot-password', { email }, { skipAuth: true });
    },
  };
}
